import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Play, Stop, Download, ArrowRight } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { decodeBlob, getPeaks, MixerPlayer, renderMixToMp3 } from "@/lib/audio";
import { toast } from "sonner";

const TRACK_COLORS = { rhythm: "#A8FF1F", melody: "#2563EB", vocal: "#F59E0B", drums: "#22C55E" };

// Framer-motion transition constants hoisted out of JSX so the animate/transition
// props don't create new object identities on every render.
const PROGRESS_TRANSITION = { duration: 0.05 };

export default function SharedProject() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const mixer = useRef(null);
  const raf = useRef(null);

  useEffect(() => {
    let cancelled = false;
    // Snapshot refs at effect-run time so the cleanup uses the same instances
    // (React StrictMode fires cleanups after remount which can otherwise reach
    // for refs that were reassigned by the new render).
    const mixerRef = mixer;
    const rafRef = raf;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/share/projects/${projectId}`);
        if (cancelled) return;
        setProject(data);
        const loaded = await Promise.all(
          data.tracks.map(async (t) => {
            try {
              const r = await axios.get(`${API}/share/files/${t.storage_path}`, { responseType: "blob" });
              const buf = await decodeBlob(r.data);
              return { ...t, buffer: buf, peaks: getPeaks(buf) };
            } catch (err) {
              logger.warn(`Shared track load failed ${t.id}:`, err?.message || err);
              return { ...t, buffer: null, peaks: [] };
            }
          })
        );
        if (!cancelled) setTracks(loaded);
      } catch (e) {
        logger.error("Failed to load shared project:", e?.message || e);
        setError(e.response?.status === 404 ? "This project isn't shared (or doesn't exist)." : "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (mixerRef.current) mixerRef.current.stop();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [projectId]);

  const maxDuration = Math.max(1, ...tracks.map((t) => t.duration || 0));

  async function togglePlay() {
    if (playing) {
      mixer.current?.stop();
      if (raf.current) cancelAnimationFrame(raf.current);
      setPlaying(false);
      setPos(0);
      return;
    }
    if (!mixer.current) mixer.current = new MixerPlayer();
    const dur = await mixer.current.play(tracks);
    setPlaying(true);
    const loop = () => {
      const p = mixer.current.currentPosition();
      setPos(p);
      if (p >= dur) { setPlaying(false); setPos(0); return; }
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
  }

  async function download() {
    if (tracks.length === 0) return;
    setDownloading(true);
    try {
      const blob = await renderMixToMp3(tracks);
      if (!blob) { toast.error("Nothing audible"); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(project?.name || "beatbox").replace(/[^a-z0-9]+/gi, "_")}.mp3`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      logger.error("Download failed:", e?.message || e);
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  }

  if (loading) {
    return <div className="min-h-screen bg-[#C7C9CC] grid place-items-center text-[#6E7378] font-mono">Loading…</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#C7C9CC] text-[#0A0A0A] grid place-items-center">
        <div className="text-center max-w-md">
          <h1 className="font-display text-7xl tracking-tighter mb-4">404</h1>
          <p className="text-[#2B2B2B] mb-6" data-testid="share-error">{error}</p>
          <Link to="/" className="text-[#0A0A0A] font-semibold underline underline-offset-4 decoration-[#A8FF1F] decoration-[3px]">← Back to Beat Box</Link>
        </div>
      </div>
    );
  }

  const pct = Math.min(1, pos / maxDuration);

  return (
    <div className="min-h-screen bg-[#C7C9CC] text-[#0A0A0A]">
      <header className="border-b border-[#B0B3B7]">
        <div className="max-w-[1300px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl tracking-tighter" data-testid="brand-logo">BEAT BOX</Link>
          <Link to="/register" data-testid="share-cta">
            <Button className="bb-pill bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A] h-11 px-5 font-semibold">
              Make your own <ArrowRight className="ml-2" size={16} weight="bold" />
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-[1300px] mx-auto px-6 lg:px-12 py-12 lg:py-20">
        <span className="bb-tag" data-testid="share-overline">SHARED PROJECT</span>
        <h1 className="font-display text-5xl lg:text-7xl tracking-tighter leading-[0.92] mt-4 mb-3" data-testid="shared-project-name">
          {project.name}
        </h1>
        <p className="text-[#2B2B2B] mb-10">
          by <strong className="text-[#0A0A0A]">{project.owner_name}</strong> · {project.bpm} BPM · {tracks.length} track{tracks.length === 1 ? "" : "s"}
        </p>

        <div className="bb-card p-6 mb-8 flex items-center gap-4">
          <Button
            onClick={togglePlay}
            disabled={tracks.length === 0}
            data-testid={playing ? "share-stop-btn" : "share-play-btn"}
            className={`rounded-full h-14 w-14 p-0 ${playing ? "bg-[#0A0A0A] text-white hover:bg-[#2B2B2B]" : "bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A]"}`}
            aria-label={playing ? "Stop" : "Play"}
          >
            {playing ? <Stop size={22} weight="fill" /> : <Play size={22} weight="fill" />}
          </Button>
          <div className="flex-1 h-2 bg-[#D0D2D5] rounded-full relative overflow-hidden">
            <motion.div className="absolute inset-y-0 left-0 bg-[#0A0A0A] rounded-full" animate={{ width: `${pct * 100}%` }} transition={PROGRESS_TRANSITION} />
          </div>
          <span className="font-mono text-[#6E7378] text-sm tabular-nums">
            {pos.toFixed(1)} / {maxDuration.toFixed(1)}s
          </span>
          <Button
            onClick={download}
            disabled={downloading || tracks.length === 0}
            data-testid="share-download-btn"
            className="bb-pill h-12 px-5 bg-white border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-[#0A0A0A] font-semibold"
          >
            <Download className="mr-2" size={16} weight="bold" /> {downloading ? "Rendering…" : "MP3"}
          </Button>
        </div>

        <div className="bb-card overflow-hidden" data-testid="shared-tracks">
          {tracks.length === 0 ? (
            <div className="p-10 text-center text-[#6E7378] font-mono">No tracks in this project.</div>
          ) : (
            tracks.map((t, idx) => {
              const c = TRACK_COLORS[t.kind] || "#A8FF1F";
              return (
                <div key={t.id} className={`grid grid-cols-12 ${idx > 0 ? "border-t border-[#D0D2D5]" : ""}`}>
                  <div className="col-span-12 md:col-span-3 p-5 border-r border-[#D0D2D5] bg-[#F5F5F5] flex items-start gap-3">
                    <div className="w-1.5 self-stretch rounded-full" style={{ background: c, minHeight: 60 }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-xl tracking-tighter truncate">{t.name}</div>
                      <div className="text-xs font-mono uppercase tracking-widest mt-1 text-[#6E7378]">
                        <span style={{ color: c }}>● </span>{t.kind} · {t.duration.toFixed(1)}s
                      </div>
                    </div>
                  </div>
                  <div className="col-span-12 md:col-span-9 relative bg-white overflow-hidden">
                    <div className="absolute inset-0 flex items-center px-4 gap-[2px]">
                      {(t.peaks || []).map((p, i) => {
                        const h = Math.max(2, p * 80);
                        const active = playing && i / (t.peaks || []).length < pct;
                        return (
                          <div key={`peak-${t.id}-${i}`} className="w-[3px] rounded-sm" style={{ height: `${h}px`, background: active ? c : `${c}77` }} />
                        );
                      })}
                    </div>
                    {playing && (
                      <motion.div className="absolute top-0 bottom-0 w-px"
                        style={{ background: "#0A0A0A", left: `${pct * 100}%` }} />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </main>

      <footer className="py-10 text-center text-[#6E7378] font-mono text-xs uppercase tracking-widest">
        Made with <span className="text-[#A8FF1F]">●</span> Beat Box — make yours at <Link to="/" className="underline">beatbox</Link>
      </footer>
    </div>
  );
}
